/**
 * Employee Web — `/auth/reset?token=...` route. AUTH-10, AUTH-15.
 *
 * UNAUTHENTICATED (NOT nested under `_auth`) — users arriving via an emailed
 * reset link must be able to complete the flow without an active session.
 *
 * `?token=...` is optional at the route level: a bare or truncated link (an
 * email client wraps/clips long URLs) previously threw to the generic router
 * error boundary. Instead we accept an empty token and hand it to ResetPage,
 * which renders the friendly "invalid link — request a new one" terminal state
 * with the existing onRequestNewLink CTA.
 *
 * D-10: successful reset does NOT auto-login — navigates to `/login` for a
 * fresh sign-in. The backend revokes all refresh families server-side on
 * reset-complete, so prior sessions are killed regardless.
 */
import type { JSX } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { GLOBAL_LOGIN_PATH, isGlobalEntryHost } from "@linkhr/auth";
import { z } from "zod";
import { ResetPage } from "../features/auth/ResetPage";

const searchSchema = z.object({
  // Optional + defaulted so a missing/truncated token renders the friendly
  // invalid-link screen instead of a raw route error boundary.
  token: z.string().optional().default(""),
});

export const Route = createFileRoute("/auth/reset")({
  validateSearch: searchSchema,
  component: ResetRouteComponent,
});

function ResetRouteComponent(): JSX.Element {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <ResetPage
      token={token}
      onSuccess={() => {
        // The shared sign-in's reset links land here whoever asked; only its
        // login can tell an admin from an employee.
        if (isGlobalEntryHost()) {
          window.location.replace(GLOBAL_LOGIN_PATH);
          return;
        }
        void navigate({ to: "/login" });
      }}
      onRequestNewLink={() => {
        void navigate({ to: "/auth/forgot" });
      }}
    />
  );
}
